import { useDraggable } from '@dnd-kit/core'
import { uniqueId } from 'lodash'
import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react'
import { ControlSize } from 'types'
import { useRefRect } from 'utils'
import { Container, Playhead } from './style'

type Props = {
    className?: string
    color: string
    disabled?: boolean
    dndId?: string
    size: ControlSize
    value?: number
    onDrag?: (value: number) => void
    onDragEnd?: (value: number) => void
    onDragStart?: (value: number) => void
}

const PlayheadDnd = ({
    className,
    color,
    disabled = false,
    dndId,
    size,
    value: valueProp,
    onDrag,
    onDragEnd,
    onDragStart,
}: Props) => {
    const idRef = useRef<string>(dndId || uniqueId('playhead'))
    const baseDraggingStateRef = useRef<boolean | undefined>(false)
    const basePointerXRef = useRef<number>(0)
    const [value, setValue] = useState<number>(valueProp || 0)

    const { attributes, isDragging, listeners, node, setNodeRef, transform } = useDraggable({
        disabled,
        id: idRef.current,
        data: {
            control: 'playhead',
            controlId: idRef.current,
            controlType: 'playhead',
            controlValue: value,
        },
    })

    const nodeRect = useRefRect(node)
    const playheadX = useMemo(() => Math.round((nodeRect?.width || 0) * value), [nodeRect?.width, value])

    const onPointerDown = (e: PointerEvent) => {
        basePointerXRef.current = e.pageX - (nodeRect?.left || 0)
        listeners?.onPointerDown?.(e)
    }

    useEffect(() => {
        if (isDragging !== baseDraggingStateRef.current) {
            baseDraggingStateRef.current = undefined
            isDragging ? onDragStart?.(value) : onDragEnd?.(value)
        }
    }, [isDragging])

    useEffect(() => {
        if (!transform || !isDragging || !nodeRect?.width) return

        const dragX = basePointerXRef.current + transform.x
        const dragValue = Math.max(0, Math.min(1, dragX / nodeRect.width))

        setValue(dragValue)
        onDrag?.(dragValue)
    }, [transform?.x])

    useEffect(() => {
        if (typeof valueProp === 'number' && !isDragging) {
            setValue(valueProp)
        }
    }, [valueProp])

    return (
        <Container
            {...attributes}
            {...listeners}
            className={className}
            disabled={disabled}
            ref={setNodeRef}
            onPointerDown={onPointerDown}
        >
            <Playhead color={color} controlSize={size} style={{ transform: `translateX(${playheadX}px)` }} />
        </Container>
    )
}

export default PlayheadDnd
